import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useIntercom } from "react-use-intercom";
import { InputAdornment } from "@mui/material";

import PersonalInformationSkeleton from "../../Components/Skeleton/personal-information-skeleton";
import TextField from "../../Components/Form Fields/text-field";
import OutlineButton from "../../Components/Buttons/outline-button";

import renderEditEmail from "../../Organisms/Modals/Personal Information/edit-email";
import renderEditPhoneNumber from "../../Organisms/Modals/Personal Information/edit-phone-number";

import { useModalContext } from "../../Contexts/ModalContext";

import { useDeals, useUserAccount } from "../../CustomHooks/Queries";

import { capitaliseString, transformCountryCodeFormatToUSNumber } from "../../Utils/helpers";

import { ArrowLeftCircle, Edit } from "../../Assets/Icons";

export default function PersonalInformation() {
  const modalContext = useModalContext();

  const navigate = useNavigate();

  const { show: showIntercom, update } = useIntercom();

  const { isLoading: isLoadingDeals, data: deals } = useDeals();
  const { isLoading: isLoadingUserAccountData, data: userAccountData } = useUserAccount();

  useEffect(() => {
    update({
      userId: userAccountData?.data?.data?.user?._id,
      email: userAccountData?.data?.data?.user?.email,
      phone: userAccountData?.data?.data?.user?.phoneNumber,
    });
  }, [userAccountData, update]);

  const user = useMemo(() => userAccountData?.data?.data?.user ?? {}, [userAccountData]);

  const lastName = useMemo(() => capitaliseString(user?.lastName), [user]);

  const phoneNumber = useMemo(() => transformCountryCodeFormatToUSNumber(user?.phoneNumber ?? ""), [user]);

  const addressLine = useMemo(() => {
    return `${user?.address1 ?? ""} ${user?.address2 ?? ""}`.trim();
  }, [user]);

  const cityLine = useMemo(() => {
    return `${!!user?.city ? `${user.city},` : ""} ${user?.state ?? ""} ${user?.zipCode ?? ""}`.trim();
  }, [user]);

  const dateOfBirth = useMemo(() => {
    const dateStr = deals?.data?.data?.currentDeal?.birthdate ?? "";
    if (dateStr === "") {
      return "";
    }

    const dateObj = new Date(dateStr);

    // Extract month, day, and year using UTC methods to avoid time zone issues
    const day = dateObj.getUTCDate();
    const month = dateObj.toLocaleString("en-US", { month: "short", timeZone: "UTC" });
    const year = dateObj.getUTCFullYear();

    return `${month} ${day}, ${year}`;
  }, [deals]);

  const handleEditEmailClick = renderEditEmail.bind(this, modalContext, user?.email);
  const handleEditPhoneClick = renderEditPhoneNumber.bind(this, modalContext, phoneNumber);

  const isLoading = isLoadingUserAccountData || isLoadingDeals;

  return (
    <div className="personal-information">
      <section className="card-section-wrapper">
        <div className="max-width-container">
          <div className="card-section">
            <div className="section-header">
              <button className="go-back-button" onClick={navigate.bind(this, -1)}>
                <ArrowLeftCircle />
              </button>
              <h3 className="section-title">Personal Information</h3>
              <div className="dummy"></div>
            </div>
            {isLoading ? (
              <PersonalInformationSkeleton />
            ) : (
              <div className="content flex-column justify-content-start gap-0">
                <TextField className="mb-sm-6 mb-4" label="First Name" value={user?.firstName} readOnly={true} />
                <TextField className="mb-sm-6 mb-4" label="Last Name" value={lastName} readOnly={true} />
                <TextField className="mb-sm-6 mb-4" label="Date Of Birth" value={dateOfBirth} readOnly={true} />
                <TextField
                  className="clickable mb-sm-6 mb-4"
                  label="Phone"
                  value={phoneNumber}
                  readOnly={true}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Edit role="button" onClick={handleEditPhoneClick} />
                      </InputAdornment>
                    ),
                  }}
                  onClick={handleEditPhoneClick}
                />
                <TextField
                  className="clickable mb-sm-6 mb-4"
                  label="Email"
                  value={user?.email}
                  readOnly={true}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Edit role="button" onClick={handleEditEmailClick} />
                      </InputAdornment>
                    ),
                  }}
                  onClick={handleEditEmailClick}
                />
                <div className="text-field dummy mb-sm-6 mb-4">
                  <label>Address</label>
                  {!!addressLine && <div className="value">{addressLine}</div>}
                  {!!cityLine && <div className="value">{cityLine}</div>}
                  {!addressLine && !cityLine && <div className="empty-state"></div>}
                </div>
                <div className="flex-grow-1"></div>
                <OutlineButton className="green-theme" onClick={showIntercom}>
                  Need to update something? Contact Support
                </OutlineButton>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
